import type { MarketSessionPhase } from 'utils/shared/time/marketSession';

export interface SessionTone {
  /** Solid dot fill. */
  dot: string;
  /** Expanding ping ring, shown only while a session is actually running. */
  ping: string;
  /** Header wash behind the panel title. */
  wash: string;
  /** The "Open" chip on a delivering row. */
  chip: string;
}

/**
 * One colour per session, and the dot means exactly one thing: which session is running.
 *
 * Tier deliberately does not enter this palette. A viewer's plan is orthogonal to whether the
 * market is open, so folding it in would make the dot ask a question ("amber because I'm delayed,
 * or because something is wrong?") instead of answering one. The delayed state is a badge on the
 * row it belongs to, next to the sentence that sells the upgrade.
 *
 * Colours come from the app theme rather than raw hex: bullish-green #17be64, accent-blue #3C8DF7,
 * graph-purple #B64EC7.
 */
export const SESSION_TONES: Record<MarketSessionPhase, SessionTone> = {
  regular: {
    dot: 'bg-bullish-green',
    ping: 'bg-bullish-green',
    wash: 'from-bullish-green/[0.13]',
    chip: 'bg-bullish-green/15 text-bullish-green',
  },
  // Pre-market and after-hours share one colour on purpose. They are the same thing to a trader —
  // the extended session, where prices move and the options tape does not — and giving them two
  // colours asks the reader to learn a distinction that changes nothing about what they can do.
  // The label still says which one it is.
  pre: {
    dot: 'bg-graph-purple',
    ping: 'bg-graph-purple',
    wash: 'from-graph-purple/[0.13]',
    chip: 'bg-graph-purple/15 text-graph-purple',
  },
  after: {
    dot: 'bg-graph-purple',
    ping: 'bg-graph-purple',
    wash: 'from-graph-purple/[0.13]',
    chip: 'bg-graph-purple/15 text-graph-purple',
  },
  closed: {
    dot: 'bg-palette-black-6',
    ping: 'bg-palette-black-6',
    wash: 'from-white/[0.04]',
    chip: 'bg-white/[0.06] text-palette-black-7',
  },
};
